import { toast } from "sonner";
import { enhanceElementsForExport, restoreOriginalStyles } from './pdf-style-enhancer';
import { generateCanvas, CanvasProgressCallback } from './canvas-generator';
import { generateAndSavePDF, PDFProgressCallback } from './pdf-generator';

/**
 * Exports the journey map as a PDF document with progressive feedback
 * Captures the map exactly as it appears on screen
 * Automatically uses landscape orientation for maps with 4+ stages
 */
export const exportJourneyAsPDF = async (
  canvasRef: React.RefObject<HTMLDivElement>, 
  zoomLevel: number, 
  setZoomLevel: (zoom: number) => void,
  mapName?: string
) => {
  if (!canvasRef.current) {
    toast.error("Cannot export: No journey map found");
    return;
  }
  
  // Check if map is empty
  const stageCards = canvasRef.current.querySelectorAll('.stage-card');
  if (stageCards.length === 0) {
    toast.error("Cannot export: Journey map is empty");
    return;
  }
  
  // Create a toast ID for updating the same toast with progress
  const toastId = "pdf-export-progress";
  
  // Store current container reference
  const containerElement = canvasRef.current;
  let originalStyles: any[] = [];
  
  try {
    // Step 1: Initialize export with orientation info
    const orientationMsg = stageCards.length > 3 ? 
      "Preparing PDF export (landscape mode)..." : 
      "Preparing PDF export (portrait mode)...";
    toast.loading(orientationMsg, { id: toastId });
    
    // Step 2: Enhance styles for export - enforcing light theme and appropriate layout
    toast.loading("Enhancing visuals for export...", { id: toastId });
    originalStyles = enhanceElementsForExport(containerElement);
    
    // Create progress callback for canvas generation
    const canvasProgressCallback: CanvasProgressCallback = (stage, progress) => {
      const stageMessages: Record<string, string> = {
        setup: "Setting up canvas...",
        rendering: "Capturing journey map...",
        enhancing: "Processing content...",
        finalizing: "Finalizing image...",
        complete: "Image capture complete"
      };
      
      const message = stageMessages[stage] || `Processing (${progress || 0}%)...`;
      toast.loading(message, { id: toastId });
    };
    
    // Step 3: Generate canvas with progress updates
    const canvas = await generateCanvas(containerElement, originalStyles, canvasProgressCallback);
    
    // Create progress callback for PDF generation
    const pdfProgressCallback: PDFProgressCallback = (stage, progress) => {
      const stageMessages: Record<string, string> = {
        converting: "Converting to PDF format...",
        formatting: "Formatting document...",
        sizing: "Optimizing layout to minimize blank space...",
        adding: "Creating document...",
        saving: "Saving PDF...",
        complete: "PDF saved successfully"
      };
      
      const message = stageMessages[stage] || `Creating PDF (${progress || 0}%)...`;
      toast.loading(message, { id: toastId });
    };
    
    // Step 4: Generate PDF with progress updates
    // Sanitize map name more thoroughly
    const sanitizedMapName = mapName && mapName.trim() ? 
      mapName.trim().replace(/[^a-zA-Z0-9\s-_]/g, '_') : 
      'journey_map';
    
    // Generate and save PDF - orientation determined by dimensions and stage count
    generateAndSavePDF(canvas, stageCards.length, sanitizedMapName, pdfProgressCallback);
    
    toast.success("PDF export complete with full-page layout!", { id: toastId });
  } catch (error) {
    console.error("Export error:", error);
    const errorMessage = error instanceof Error ? 
      `Failed to export PDF: ${error.message}` : 
      "Failed to export PDF";
    toast.error(errorMessage, { id: toastId });
  } finally {
    // Restore styles immediately but in a try-catch to handle any issues
    try {
      if (originalStyles.length > 0) {
        restoreOriginalStyles(originalStyles);
      }
    } catch (styleError) {
      console.error("Error restoring styles:", styleError);
    }
  }
};
